import type { ReactNode } from "react";
import { Icon } from "@keydials/dial-workspace";
import type { DialSettingsItem, DialSettingsSection } from "./dialSettingsNav";

/**
 * The Dial Settings shell: a menu of sections, and whichever one is open.
 *
 * Started life (KEY-455) as a page you navigated away from the dial to reach, with a "Back to dial"
 * button at the top. It is now the dial overview's last tab, so that button is gone — you leave by
 * clicking another tab, and the dial itself is still on screen beside it. Everything else is unchanged.
 *
 * Deliberately dumb — it takes the menu, the open section and the content to show. Every setting here is a
 * write against the dial, and the loader that owns those writes (and the cross-org scoping, and the kiosk
 * idle lock) is `Workspace`. Giving this panel its own data layer would mean a second one to keep honest.
 */
export function DialSettingsPanel({
  items,
  section,
  onSelect,
  children,
}: {
  items: DialSettingsItem[];
  section: DialSettingsSection;
  onSelect: (next: DialSettingsSection) => void;
  children: ReactNode;
}) {
  const open = items.find((i) => i.id === section) ?? items[0];

  return (
    // Menu beside content on a wide screen, stacked on a narrow one — at the same 920px breakpoint the
    // dial itself uses, so the app doesn't reflow at two different widths.
    <div className="kd-settings-split">
      <nav aria-label="Dial settings" style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        {items.map((item) => {
          const active = item.id === section;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={active ? "page" : undefined}
              data-help-anchor={item.helpAnchorId}
              className="kd-row-hover"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: "none",
                textAlign: "left",
                cursor: "pointer",
                background: active ? "var(--surface-inset)" : "transparent",
                color: active ? "var(--text)" : "var(--text-muted)",
              }}
            >
              <Icon name={item.icon} size={16} style={{ marginTop: 2, flex: "none", color: active ? "var(--accent-text)" : "var(--text-faint)" }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: active ? 700 : 600 }}>{item.label}</span>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--text-faint)", marginTop: 2, lineHeight: 1.45 }}>
                  {item.hint}
                </span>
              </span>
            </button>
          );
        })}
      </nav>

      <section aria-label={open?.label ?? "Settings"} style={{ display: "flex", flexDirection: "column", gap: "var(--gap-stack)", minWidth: 0 }}>
        {children}
      </section>
    </div>
  );
}
